import React, { useContext, useEffect, useState, useMemo } from "react";
import { Context } from "../../../../Context";
import operationData from "../../../../../Operation/data.json";
import { fetchStoreStatus, updateStoreStatus } from "../../../../utils/foodDeliveryApi";
import FoodDeliveryTabs from "./FoodDeliveryTabs";

const GSTDetails = () => {
  const { institutionData } = useContext(Context);
  const institution = operationData.foodDeliveryInstitution;
  const activeInstitution = institution || institutionData?.institution || "";

  const [gstNumber, setGstNumber] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessAddress, setBusinessAddress] = useState("");
  const [gstRate, setGstRate] = useState("5");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const loadGSTDetails = async () => {
    if (!activeInstitution) return;
    setLoading(true);
    try {
      const res = await fetchStoreStatus(activeInstitution);
      const data = res?.data || res || {};
      setGstNumber(data.gstNumber || data.gstin || "");
      setBusinessName(data.businessName || data.institutionName || "");
      setBusinessAddress(data.businessAddress || data.address || "");
      setGstRate(data.gstRate || "5");
    } catch (err) {
      setError(err?.message || "Failed to load GST details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGSTDetails();
  }, [activeInstitution]);

  const handleSave = async () => {
    if (!gstNumber.trim()) {
      setError("GST Number is required.");
      return;
    }
    if (!businessName.trim()) {
      setError("Business Name is required.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const payload = {
        gstNumber: gstNumber.trim(),
        gstin: gstNumber.trim(),
        businessName: businessName.trim(),
        businessAddress: businessAddress.trim(),
        gstRate: Number(gstRate) || 5,
      };
      await updateStoreStatus(activeInstitution, payload);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err?.message || "Failed to save GST details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-3xl space-y-6">
          {/* Header */}
          <div className="bg-white px-6 py-6 rounded-lg shadow-sm border border-gray-200">
            <h1 className="text-2xl font-extrabold text-[#1F2937]">Food Delivery — GST Details</h1>
            <p className="mt-1 text-sm text-gray-500">Manage tax and GST information • {activeInstitution}</p>
          </div>

          {/* Messages */}
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}
          {success && (
            <div className="px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
              ✓ GST details saved successfully!
            </div>
          )}

          {/* Form */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">GST Number (GSTIN) *</label>
              <input
                type="text"
                placeholder="e.g., 27AAPTU5055K1Z0"
                value={gstNumber}
                onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
              <p className="text-xs text-gray-500 mt-1">Format: 2 digits state code + 10 digit PAN + 1 digit entity code + 1 check digit</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Business Name *</label>
              <input
                type="text"
                placeholder="Your business/restaurant name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Business Address</label>
              <textarea
                placeholder="Complete business address"
                value={businessAddress}
                onChange={(e) => setBusinessAddress(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">GST Rate (%)</label>
              <select
                value={gstRate}
                onChange={(e) => setGstRate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="0">0% (Exempt)</option>
                <option value="5">5%</option>
                <option value="12">12%</option>
                <option value="18">18%</option>
                <option value="28">28%</option>
              </select>
            </div>

            <div className="flex gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 rounded-lg bg-gray-900 text-white px-4 py-2.5 font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {loading ? "Saving..." : "Save GST Details"}
              </button>
              <button
                onClick={() => loadGSTDetails()}
                disabled={loading}
                className="rounded-lg bg-gray-200 text-gray-900 px-4 py-2.5 font-semibold hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Reset
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">ℹ️ GST Information</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• GST Number must be 15 characters (alphanumeric)</li>
              <li>• This information will be shown on invoices and receipts</li>
              <li>• GST Rate determines the tax applied to orders</li>
              <li>• Contact your accountant for the correct GST rate for your business</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GSTDetails;
